/**
 * WireframeWrapper - Provides context for all wireframe pages
 * Includes ToastProvider for notifications, TutorialProvider for interactive tutorials,
 * HumChat AI assistant available on all pages, and DemoHeader for consistent branding
 *
 * When embedded (via ?embedded=true query param), hides only DemoHeader (navigation header)
 * but keeps HumChat AI assistant and Tutorial overlays for full interactive functionality
 */

import { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ToastProvider } from './WireframeUI';
import { TutorialProvider, TutorialOverlay } from './WireframeTutorial';
import { HumChatProvider, HumChatSidebar } from './GlobalHumChat';
import { DemoHeader } from './DemoHeader';

interface WireframeWrapperProps {
  children: ReactNode;
}

export function WireframeWrapper({ children }: WireframeWrapperProps) {
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embedded') === 'true';

  // In embedded mode (landing page iframe), hide only DemoHeader but keep HumChat and Tutorial
  if (isEmbedded) {
    return (
      <ToastProvider>
        <TutorialProvider>
          <HumChatProvider>
            {/* No DemoHeader, no top padding - but keep HumChat and Tutorial */}
            {children}
            <HumChatSidebar />
            <TutorialOverlay />
          </HumChatProvider>
        </TutorialProvider>
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <TutorialProvider>
        <HumChatProvider>
          <DemoHeader />
          <div className="pt-16"> {/* Offset for fixed header */}
            {children}
          </div>
          <HumChatSidebar />
          <TutorialOverlay />
        </HumChatProvider>
      </TutorialProvider>
    </ToastProvider>
  );
}

export default WireframeWrapper;
